import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type PriceChange = {
  id: string;
  changed_at: string;
  changed_by_email: string;
  server_type: string;
  display_name: string;
  action: string;
  changes: Record<string, any>;
};

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function describe(entry: PriceChange) {
  const name = entry.display_name ? `${entry.display_name} (${entry.server_type})` : entry.server_type;
  const where = entry.changes.region_name ?? entry.changes.region ?? '';
  const price = `${entry.changes.monthly_price} ${entry.changes.currency}/mo${where ? ` in ${where}` : ''}`;
  if (entry.action === 'price_removed') {
    return `${name}: ${price} from ${day(entry.changes.effective_date)} removed`;
  }
  return `${name}: ${price} from ${day(entry.changes.effective_date)}${entry.changes.notes ? ` · ${entry.changes.notes}` : ''}`;
}

export default function PriceChangeLog() {
  const changes = useQuery({
    queryKey: ['hetzner-offering-changes'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/hetzner-configs/changes/')).data as PriceChange[];
    },
  });

  const priceChanges = (changes.data ?? []).filter(
    (entry) => entry.action === 'price_added' || entry.action === 'price_removed',
  );

  return (
    <Card data-testid="price-change-log">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4" />
          Recent price changes
        </CardTitle>
        <CardDescription>
          Every cloud server price set or removed, and by whom. Changes to the
          sizes themselves are on the Server Configs page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {priceChanges.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No price changes recorded yet.</p>
        ) : (
          <ol className="space-y-2" data-testid="price-change-list">
            {priceChanges.map((entry) => (
              <li key={entry.id} className="rounded-md border p-3 text-sm" data-testid={`price-change-${entry.id}`}>
                <span className="font-medium">
                  {new Date(entry.changed_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
                <span className="text-muted-foreground"> · {entry.changed_by_email || 'a global admin'} · </span>
                {describe(entry)}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
